import {
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAuthStore } from '../../store/storeAuth';
import { AiTextGeneratorFormValues } from '../AiConfigs/constants';
import SliderField from './SliderField';

type Props = {
  watch: AiTextGeneratorFormValues;
  onChange: (e: any) => void;
  systems?: any[];
};

const Anthropic = ({ onChange, watch, systems }: Props) => {
  const { user } = useAuthStore(state => state);
  const isDark = user?.theme === 'dark';
  const anthropicModels =
    systems
      ?.find((item: any) => item?.system === 'anthropic')
      ?.models?.filter((m: any) => m.type === 'Text') || [];
  return (
    <>
      <Box mt="4px">
        <Typography
          fontSize="14px"
          fontWeight={500}
          sx={{ mb: '4px', color: isDark ? '#D0D5DD' : 'black' }}
          variant="body1"
        >
          Model
        </Typography>
        <FormControl sx={{ width: '100%' }}>
          <Select
            className="nodrag"
            name="anthropicModel"
            fullWidth
            size="small"
            value={watch.anthropicModel}
            sx={{
              height: 'unset',
              bgcolor: isDark ? '#1D2939' : '#F9FAFB',
              color: isDark ? '#D0D5DD' : '#101828',
            }}
            onChange={onChange}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: '400px',
                  maxWidth: '600px',
                },
              },
            }}
          >
            {anthropicModels.map((opt: any, index: number) => (
              <MenuItem
                className="nodrag"
                key={opt.model + index}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'start',
                  padding: '10px 14px',
                  whiteSpace: 'normal',
                }}
                value={opt.model}
                disabled={opt.enabled === false || opt.disabledByCompany}
              >
                <Box display="flex" alignItems="center" gap="6px">
                  <Typography
                    fontSize="13px"
                    fontWeight={500}
                    textTransform="capitalize"
                    color={isDark ? '#D0D5DD' : 'black'}
                  >
                    {opt.displayName || opt.model}
                  </Typography>
                  {opt.disabledByCompany && (
                    <Typography fontSize="11px" color="red">
                      (disabled by admin)
                    </Typography>
                  )}
                </Box>
                {opt?.tags?.length > 0 && (
                  <Box display="flex" gap="4px" alignItems="center" flexWrap="wrap" mt="4px">
                    {opt.tags.map((tag: any, tagIndex: number) => {
                      const tagLabel = typeof tag === 'string' ? tag : tag.label;
                      const tagTooltip = typeof tag === 'string' ? '' : tag.tooltip;
                      return (
                        <Tooltip key={tagLabel + tagIndex} title={tagTooltip} arrow placement="top">
                          <Chip
                            size="small"
                            sx={{
                              borderRadius: '4px',
                              fontSize: '11px',
                              height: '20px',
                              border: `1px solid ${isDark ? '#475467' : '#EAECF0'}`,
                              color: isDark ? '#D0D5DD' : 'black',
                            }}
                            label={tagLabel}
                          />
                        </Tooltip>
                      );
                    })}
                  </Box>
                )}
                {opt.description && (
                  <Typography
                    fontSize="12px"
                    sx={{
                      color: '#667085',
                      mt: '4px',
                      whiteSpace: 'normal',
                      lineHeight: 1.4,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {opt.description}
                  </Typography>
                )}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: '20px',
        }}
      >
        <FormControlLabel
          control={
            <Switch
              onChange={() => {
                onChange({
                  target: { name: 'autoToken', value: !watch.autoToken },
                });
              }}
              name="autoToken"
              checked={watch.autoToken}
            />
          }
          label="Auto setting"
        />
        <FormControl>
          <TextField
            type="number"
            onChange={onChange}
            value={watch.autoToken ? 0 : watch.anthropicToken}
            disabled={watch.autoToken}
            className="nodrag"
            label="Output Token Max"
            name="anthropicToken"
            InputProps={{
              style: { color: isDark ? '#D0D5DD' : '#101828' },
            }}
            sx={{
              width: '290px',
              color: isDark ? '#D0D5DD' : '#101828',
              bgcolor: isDark ? '#1D2939' : '#F9FAFB',
              border: `2px solid ${isDark ? '#475467' : '#EAECF0'}`,
            }}
          />
        </FormControl>
      </Box>
      <SliderField
        label="Top K"
        name="anthropicTopK"
        value={Number(watch.anthropicTopK)}
        onChange={onChange}
        min={1}
        max={40}
        step={1}
      />
      <SliderField
        label="Top P"
        name="anthropicTopP"
        value={Number(watch.anthropicTopP)}
        onChange={onChange}
        min={0}
        max={1}
        step={0.1}
      />
      <SliderField
        label="Temperature"
        name="anthropicTemperature"
        value={Number(watch.anthropicTemperature)}
        onChange={onChange}
        min={0}
        max={1}
        step={0.1}
      />
    </>
  );
};

export default Anthropic;
